/**
 * Bulk reclassify (FR-CAT-09, FR-CAT-10).
 *
 * The screen that gets the **180 rows (28%) that import as nothing but the word
 * "Expenses"** cleaned up. Filter to a set, select, assign category / subcategory / tags
 * in one action.
 *
 * Pure: deciding what each transaction *becomes* is the part that can be quietly wrong,
 * so it lives here with tests rather than inside a Firestore batch.
 *
 * Three rules that are easy to get wrong and are enforced here:
 *
 *  1. **A transfer carries no category** (FR-TXN-05, and `buildTransaction` nulls it
 *     anyway). Assigning one to a transfer would look like it worked and change nothing,
 *     so a transfer in the selection is skipped with a reason rather than silently no-op'd.
 *  2. **A subcategory belongs to exactly one parent.** Moving a transaction to a new
 *     category while leaving the old subcategory attached produces a row filed under
 *     "Groceries → School Fee". The old subcategory is dropped unless it is a child of
 *     the new category.
 *  3. **Income heads and expense categories are not interchangeable** (FR-CAT-03). A
 *     selection spanning both types applies to the matching ones and reports the rest.
 *
 * The plan is produced whole, before anything is written, so the count shown on the
 * button is the count that actually changes — not an optimistic total that quietly
 * includes rows the write will skip.
 */

import type { Category } from './categories';
import type { Transaction } from './transactions';

export interface Reclassification {
  /** Present-but-undefined means "leave alone"; `null` means "clear". */
  categoryId?: string | null;
  subcategoryId?: string | null;
  addTagIds?: string[];
  removeTagIds?: string[];
  /**
   * Clear the follow-up flag (FR-TXN-20).
   *
   * Opt-in, because a row flagged as a suspected duplicate is not resolved by giving it
   * a category — but without this the worklist can never be emptied except one row at a
   * time, which is the exact thing this screen exists to avoid.
   */
  clearNeedsEnrichment?: boolean;
}

/** The fields this screen may touch. Nothing else is writable from here. */
export const RECLASSIFY_FIELDS = [
  'categoryId',
  'subcategoryId',
  'tagIds',
  'needsEnrichment',
] as const;

export type ReclassifyField = (typeof RECLASSIFY_FIELDS)[number];

export interface PlannedChange {
  before: Transaction;
  after: Transaction;
  /** Only what actually differs — this is what gets written and what gets audited. */
  patch: Pick<Transaction, ReclassifyField>;
  fields: ReclassifyField[];
}

export interface SkippedTransaction {
  transaction: Transaction;
  reason: string;
}

export interface ReclassifyPlan {
  changes: PlannedChange[];
  /** Selected, eligible, but already in the requested state. */
  unchanged: Transaction[];
  skipped: SkippedTransaction[];
}

export function isEmptyChange(change: Reclassification): boolean {
  return (
    !('categoryId' in change) &&
    !('subcategoryId' in change) &&
    !change.addTagIds?.length &&
    !change.removeTagIds?.length &&
    !change.clearNeedsEnrichment
  );
}

/**
 * Is this change coherent at all?
 *
 * Returns a message rather than throwing, so the UI can keep Apply disabled and say why
 * instead of failing at the moment of the click.
 */
export function validateReclassification(
  change: Reclassification,
  categories: Category[],
): string | null {
  if (isEmptyChange(change)) return 'Choose a category or a tag to apply.';

  const find = (id: string) => categories.find((c) => c.id === id);

  if (change.categoryId) {
    const category = find(change.categoryId);
    if (!category) return 'That category no longer exists.';
    if (category.parentId !== null) {
      return `"${category.name}" is a subcategory. Pick its parent as the category.`;
    }
  }

  if (change.subcategoryId) {
    const subcategory = find(change.subcategoryId);
    if (!subcategory) return 'That subcategory no longer exists.';
    if (subcategory.parentId === null) {
      return `"${subcategory.name}" is a top-level category, not a subcategory.`;
    }
    // Without a stated parent the subcategory would have to imply one, and the implied
    // parent would differ per transaction. Requiring both keeps the result predictable.
    if (!change.categoryId) {
      return `Choose the parent category for "${subcategory.name}" as well.`;
    }
    if (subcategory.parentId !== change.categoryId) {
      return `"${subcategory.name}" does not belong to the chosen category.`;
    }
  }

  const both = (change.addTagIds ?? []).filter((id) => change.removeTagIds?.includes(id));
  if (both.length > 0) return 'A tag cannot be added and removed in the same action.';

  return null;
}

/**
 * What each selected transaction becomes.
 *
 * `after` is built by spreading `before` rather than by rebuilding from a
 * `TransactionInput`: `TransactionInput` has no `attachments` field, so a round trip
 * through `buildTransaction` would silently drop receipts from every reclassified row.
 */
export function planReclassify(
  selected: Transaction[],
  change: Reclassification,
  categories: Category[],
): ReclassifyPlan {
  const plan: ReclassifyPlan = { changes: [], unchanged: [], skipped: [] };

  const byId = new Map(categories.map((c) => [c.id, c]));
  const target = change.categoryId ? byId.get(change.categoryId) : undefined;
  const assigningCategory = 'categoryId' in change || 'subcategoryId' in change;

  for (const before of selected) {
    if (assigningCategory && before.type === 'transfer') {
      plan.skipped.push({
        transaction: before,
        reason: 'A transfer carries no category — it is in neither income nor expenses.',
      });
      continue;
    }

    if (target) {
      const wanted = before.type === 'income' ? 'income' : 'expense';
      if (target.kind !== wanted) {
        plan.skipped.push({
          transaction: before,
          reason:
            wanted === 'income'
              ? `"${target.name}" is an expense category; this row is income.`
              : `"${target.name}" is an income head; this row is an expense.`,
        });
        continue;
      }
    }

    const after = applyChange(before, change, byId);
    const fields = changedFields(before, after);

    if (fields.length === 0) {
      plan.unchanged.push(before);
      continue;
    }

    plan.changes.push({
      before,
      after,
      patch: {
        categoryId: after.categoryId,
        subcategoryId: after.subcategoryId,
        tagIds: after.tagIds,
        needsEnrichment: after.needsEnrichment,
      },
      fields,
    });
  }

  return plan;
}

function applyChange(
  before: Transaction,
  change: Reclassification,
  categories: Map<string, Category>,
): Transaction {
  const categoryId = 'categoryId' in change ? (change.categoryId ?? null) : before.categoryId;

  let subcategoryId: string | null;
  if ('subcategoryId' in change) {
    subcategoryId = change.subcategoryId ?? null;
  } else if (categoryId !== before.categoryId) {
    // Rule 2: the existing subcategory survives a category change only if it is a child
    // of where the row is going.
    const existing = before.subcategoryId ? categories.get(before.subcategoryId) : undefined;
    subcategoryId = existing && existing.parentId === categoryId ? before.subcategoryId : null;
  } else {
    subcategoryId = before.subcategoryId;
  }

  return {
    ...before,
    categoryId,
    subcategoryId,
    tagIds: applyTags(before.tagIds, change),
    needsEnrichment: change.clearNeedsEnrichment ? false : before.needsEnrichment,
  };
}

/** Remove, then add. Order is preserved and duplicates collapse. */
function applyTags(current: string[], change: Reclassification): string[] {
  const remove = new Set(change.removeTagIds ?? []);
  const kept = current.filter((id) => !remove.has(id));
  const result = [...kept];
  for (const id of change.addTagIds ?? []) {
    if (!result.includes(id)) result.push(id);
  }
  return result;
}

function changedFields(before: Transaction, after: Transaction): ReclassifyField[] {
  const fields: ReclassifyField[] = [];
  if (before.categoryId !== after.categoryId) fields.push('categoryId');
  if (before.subcategoryId !== after.subcategoryId) fields.push('subcategoryId');
  if (!sameIds(before.tagIds, after.tagIds)) fields.push('tagIds');
  if (before.needsEnrichment !== after.needsEnrichment) fields.push('needsEnrichment');
  return fields;
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

// ---------------------------------------------------------------------------
// Describing the action, before it happens
// ---------------------------------------------------------------------------

/**
 * A plain sentence for the confirmation.
 *
 * WD-06's warning applies here: select-all covers the whole filtered set, not the visible
 * page, and the count has to be stated in words — "Apply to 180 transactions" is the only
 * thing standing between the user and an unintended mass edit.
 */
export function describeReclassification(
  change: Reclassification,
  categories: Category[],
  tagName: (id: string) => string = (id) => id,
): string {
  const name = (id: string) => categories.find((c) => c.id === id)?.name ?? id;
  const parts: string[] = [];

  if ('categoryId' in change) {
    parts.push(
      change.categoryId
        ? `set category to ${name(change.categoryId)}${
            change.subcategoryId ? ` → ${name(change.subcategoryId)}` : ''
          }`
        : 'clear the category',
    );
  } else if (change.subcategoryId) {
    parts.push(`set subcategory to ${name(change.subcategoryId)}`);
  }

  if (change.addTagIds?.length) {
    parts.push(`add ${change.addTagIds.map((id) => `#${tagName(id)}`).join(', ')}`);
  }
  if (change.removeTagIds?.length) {
    parts.push(`remove ${change.removeTagIds.map((id) => `#${tagName(id)}`).join(', ')}`);
  }
  if (change.clearNeedsEnrichment) parts.push('clear the follow-up flag');

  return parts.join(', ');
}
